package com.macrofocus.eflux.ui.data

import com.macrofocus.common.selection.MutableSelection
import com.macrofocus.common.selection.MutableSingleSelection
import com.macrofocus.eflux.DataLoader
import com.macrofocus.eflux.network.coordinate.Coordinate
import com.macrofocus.eflux.ui.data.risk.*
import mui.icons.material.QuestionMarkTwoTone
import mui.material.*
import mui.material.Size
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.sx
import org.mkui.react.useProperty
import org.mkui.react.useSelection
import org.mkui.react.useSingleSelection
import react.*
import web.cssom.*
import web.cssom.LineStyle.Companion.solid
import web.timers.Timeout
import web.timers.clearTimeout
import web.timers.setTimeout
import kotlin.time.Duration.Companion.milliseconds

external interface CoordinateChipProps : Props {
    var dataLoader: DataLoader
    var primary: Boolean?
    var probing: MutableSingleSelection<Coordinate>
    var selection: MutableSelection<Coordinate>
    var coordinate: Coordinate?
}

val CoordinateChip = FC<CoordinateChipProps> { props ->
    val theme = useTheme<Theme>()

    val dataLoader = props.dataLoader
    val controller = dataLoader.controller

    val (network, _) = useProperty(dataLoader.substationNetwork)
    val coordinate = props.coordinate
    val serif = false

    val (probed, _) = useSingleSelection(props.dataLoader.controller.coordinateProbing)
    val (selected, _) = useSelection(props.dataLoader.controller.coordinateSelection)

    val clickTimer = useRef<Timeout>(null)

    if(coordinate != null) {
        val seq = coordinate.seq
        val longitude = coordinate.physicalGeometry?.x
        val latitude = coordinate.physicalGeometry?.y

        Tooltip {
            if(coordinate.risk_map != null && coordinate.risk_map > 0) {
                title = ReactNode("${coordinate.risk_list} #$seq $latitude,$longitude")
            } else {
                title = ReactNode("$latitude,$longitude")
            }
            disableHoverListener = true
//            sx {
//                pointerEvents = None.none
//            }

            Box {
                sx {
                    display = Display.inlineBlock
                    cursor = Cursor.pointer
                }

                onPointerEnter = {
                    if(props.primary != null) controller.probeCoordinate(coordinate, props.primary!!)
                }
                onPointerLeave = {
                    if(props.primary != null) controller.probeCoordinate(null, props.primary!!)
                }
                onClick = {
                    // cancel previous timer
                    clickTimer.current?.let {
                        clearTimeout(it)
                    }

                    // start new one
                    clickTimer.current = setTimeout(250.milliseconds)  {
                        dataLoader.overviewZoom(coordinate.logicalGeometry, coordinate.physicalGeometry)
                        if(props.primary != null) controller.selectCoordinate(coordinate, props.primary!!)
                    }
                }
                onDoubleClick = {
                    // cancel pending single click
                    clickTimer.current?.let { clearTimeout(it) }

                    dataLoader.physicalZoom(coordinate.physicalGeometry)
                    if(props.primary != null) controller.selectCoordinate(coordinate, props.primary!!)
                }

                Chip {
                    sx {
                        if(serif) {
                            textTransform = None.none
                            fontSize = FontSize.xxSmall
                        } else {
                            fontFamily = "'JetBrains Mono', monospace".unsafeCast<FontFamily>()
    //                        if(seq == null || seq < 100) {
                                fontSize = FontSize.xxSmall
    //                        } else {
    //                            fontSize = 8.px
    //                        }
                        }
    //                    backgroundColor = dataLoader.getColor(coordinate, theme)
                        width = 28.px
                        height = 26.px
                        borderRadius = 50.pct
                        padding = 0.px
                        justifyContent = JustifyContent.center

                        // So that number >= 100 display
                        "& .MuiChip-label" {
                            paddingLeft = 4.px
                            paddingRight = 4.px
                        }

                        // Liquid
                        background = rgb(255, 255, 255, 0.2)
                        backdropFilter = blur(10.px)
                        if(probed?.coordinateId == coordinate.coordinateId) {
                            border = Border(1.px, solid, rgb(253, 255, 180, 255 / 255.0))
                        } else if(selected.any { it.coordinateId == coordinate.coordinateId }) {
                            border = Border(1.px, solid, rgb(253, 255, 180, 200 / 255.0))
                        }
    //                    borderRadius = 16.px
                        boxShadow = BoxShadow(0.px, 4.px, 30.px, rgb(0, 0, 0, 0.1))
                        if(probed?.coordinateId == coordinate.coordinateId) {
                            background = rgb(253, 255, 180, 153 / 255.0)
                        } else if(selected.any { it.coordinateId == coordinate.coordinateId }) {
                            background = rgb(253, 255, 180, 127 / 255.0)
                        } else {
                            backgroundImage = linearGradient(
                                angle = 135.deg,
                                rgb(255, 255, 255, 0.4),
                                rgb(255, 255, 255, 0.1)
                            )
                        }
                        hover {
    //                    background = props.tintColor?.let { Color(it).alpha(0.3) } ?: rgb(255, 255, 255, 0.3)
                            background = rgb(253, 255, 180, 153 / 255.0)
                            boxShadow = BoxShadow(0.px, 6.px, 40.px, rgb(0, 0, 0, 0.15))
                        }
                        transition = Transition(
                            property = TransitionProperty.all,
                            duration = 0.3.s,
                            timingFunction = TransitionTimingFunction.easeInOut
                        )
//                        pointerEvents = "bounding-box".unsafeCast<PointerEvents>()
                    }

    //                label = ReactNode("$seq".padStart(2, '0'))
                    if(coordinate.risk_map != null && coordinate.risk_map > 0) {
                        icon = if (coordinate.risk_map and 512 != 0) TemperatureIcon.create()
                        else if (coordinate.risk_map and 256 != 0) WindGustsIcon.create()
                        else if (coordinate.risk_map and 32 != 0) MudslideIcon.create()
                        else if (coordinate.risk_map and 16 != 0) FloodingIcon.create()
                        else if (coordinate.risk_map and 4 != 0) LightningExpectedIcon.create()
                        else if (coordinate.risk_map and 2 != 0) LightningActivityIcon.create()
                        else if (coordinate.risk_map and 1 != 0) LightningHitIcon.create()
                        else QuestionMarkTwoTone.create()
                    } else {
                        label = ReactNode("$seq")
                    }
                    color = if (coordinate.probability != null) {
                        if (coordinate.probability >= 0.1) ChipColor.error
                        else if (coordinate.probability >= 0.05) ChipColor.warning
                        else if (coordinate.probability > 0.0) ChipColor.info
                        else ChipColor.default
                    } else ChipColor.default

                    variant = ChipVariant.outlined
                    size = Size.small
                }
            }
        }
    }
}